import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

/* Components */
import { FontSubtitle24, FontTitle24, FontSubtitle16 } from "../../../../common/components/fonts";
import { LogoLoading } from "../../../../common/components/loading";
import FirmMarketingProspectRecord from "../components/prospectrecord";

/* Middleware */
import { tryGetMyLeads, tryUpdateStatus } from "../middleware/prospects";
import { ToolTipBody } from "../../../../common/components/messages";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ToolTipContainer } from "../../certification/components/directoryform";
import { Arrow, Background, Dropdown, DropdownList, DropdownRow, DropdownTitle, ListButton, Relative } from "../components/prospectelements";
import LeadRequestStatus from "../components/prospects/prospectStatus";

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    @media screen and (max-width: 768px) {
        flex-direction: column;
        gap: 20px;
        align-items: flex-start;
    }
`;
const ColProspectParent = styled.div`
    margin-top: 50px;
    display: flex;
    flex-direction: column;
    gap: 30px;
`;

const LoadingCenter = styled.div`
    margin: 60px auto;
    text-align: center;
`;

export default function FirmMarketingProspects() {
    const [filter, setFilter] = useState("All");
    const [filterOpen, setFilterOpen] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [presentedCount, setPresentedCount] = useState(0);
    const [selectedCount, setSelectedCount] = useState(0);
    const [hoverMatched, setHoverMatched] = useState(false);
    const [hoverChosen, setHoverChosen] = useState(false);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const leadData = await tryGetMyLeads();
        const leads = leadData?.leads ?? [];
        setProspects(leads);
        setPresentedCount(leadData?.timesShownToLeads ? leadData?.timesShownToLeads : 0);
        setSelectedCount(leads.length);
        setLoading(false);
    };

    const toggleFilterChange = (value) => {
        setFilter(value);
        setFilterOpen(false);
    };

    const filteredProspects = useMemo(() => {
        const value = filter;
        if (prospects && prospects.length > 0) {
            if (value === "Won") {
                return prospects.filter((p) => p.statusCode === LeadRequestStatus.MATCHED.value);
            } else if (value === "Lost") {
                return prospects.filter((p) => p.statusCode === LeadRequestStatus.ADVISOR_DID_NOT_WIN.value);
            } else if (value === "Pending") {
                return prospects.filter((p) => p.statusCode === LeadRequestStatus.SENT_TO_ADVISOR.value);
            } else {
                return prospects;
            }
        }
    }, [filter, prospects]);

    const toggleFilterOpen = () => {
        setFilterOpen(true);
    };

    const toggleFilterClose = () => {
        setFilterOpen(false);
    };

    const selectStatus = async (leadId, leadStatus) => {
        setLoading(true);
        await tryUpdateStatus(leadId, leadStatus);
        await init();
        setLoading(false);
    };

    if (isLoading) {
        return (
            <LoadingCenter>
                <LogoLoading />
            </LoadingCenter>
        );
    } else {
        return (
            <>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: "2em" }}>
                    <ToolTipContainer>
                        <ToolTipBody show={hoverMatched} style={{ left: "53%" }}>
                            <FontSubtitle16>A match occurs whenever your profile is viewed by a prospect who has completed the questionnaire.</FontSubtitle16>
                        </ToolTipBody>
                        <FontSubtitle24>
                            {presentedCount} Times Matched{" "}
                            <span
                                style={{ width: "50px", height: "50px", fontSize: "16px" }}
                                onMouseEnter={() => setHoverMatched(true)}
                                onMouseLeave={() => setHoverMatched(false)}
                            >
                                <InfoCircleOutlined />{" "}
                            </span>
                        </FontSubtitle24>
                    </ToolTipContainer>
                    <ToolTipContainer>
                        <ToolTipBody show={hoverChosen}>
                            <FontSubtitle16>Chosen refers to when a prospect selects to share their information with you.</FontSubtitle16>
                        </ToolTipBody>
                        <FontSubtitle24>
                            {" "}
                            {selectedCount} Times Chosen{" "}
                            <span style={{ width: "25px", height: "25px", fontSize: "16px" }} onMouseEnter={() => setHoverChosen(true)} onMouseLeave={() => setHoverChosen(false)}>
                                <InfoCircleOutlined />{" "}
                            </span>
                        </FontSubtitle24>
                    </ToolTipContainer>
                </div>
                <hr />

                <div style={{ marginTop: "3em" }}>
                    <Row>
                        <FontSubtitle24 spaced={true}>
                            {filter.toUpperCase()} PROSPECTS ({filteredProspects ? filteredProspects.length : 0})
                        </FontSubtitle24>
                        <Relative>
                            <Dropdown onClick={toggleFilterOpen}>
                                <DropdownRow>
                                    <DropdownTitle>{filter}</DropdownTitle>
                                    <Arrow />
                                </DropdownRow>
                            </Dropdown>
                            {filterOpen && (
                                <>
                                    <DropdownList>
                                        <ListButton onClick={() => toggleFilterChange("All")}>All</ListButton>
                                        <ListButton onClick={() => toggleFilterChange("Pending")}>Pending</ListButton>
                                        <ListButton onClick={() => toggleFilterChange("Won")}>Won</ListButton>
                                        <ListButton onClick={() => toggleFilterChange("Lost")}>Lost</ListButton>
                                    </DropdownList>
                                    <Background onClick={toggleFilterClose} />
                                </>
                            )}
                        </Relative>
                    </Row>
                    <ColProspectParent>
                        {filteredProspects?.length === 0 && <FontTitle24>No prospects found</FontTitle24>}
                        {filteredProspects?.map((p, pIndex) => {
                            return <FirmMarketingProspectRecord key={pIndex} prospect={p} selectStatus={selectStatus} index={pIndex} />;
                        })}
                    </ColProspectParent>
                </div>
            </>
        );
    }
}
